import React from "react";
import { Box, Button, Paper, Skeleton, Typography } from "@mui/material";
import { EstadoChip } from "../../../components/base";
import { formatearMonto } from "../../../helpers/formato";
import { estadoDeCredito, fechaDeCredito } from "../comunes";

// Respeta "reducir movimiento": el esqueleto queda quieto.
const sinAnimacion = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const Dato = ({ etiqueta, children }) => (
  <Box component="div" sx={{ minWidth: 0 }}>
    <Typography
      component="dt"
      variant="caption"
      sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}
    >
      {etiqueta}
    </Typography>
    <Box component="dd" sx={{ m: 0, mt: 0.5 }}>
      {children}
    </Box>
  </Box>
);

/**
 * Resumen del crédito bajo el encabezado, con lo que ya se cargó (sin pedir nada nuevo).
 *
 * @param {object} props
 * @param {object|""} props.credito Datos del crédito; vacío mientras no han llegado.
 * @param {number|null} [props.saldo] Saldo pendiente; solo se conoce cuando se cargó el plan de pagos.
 * @param {boolean|null} [props.mora] true/false cuando se cargó el plan de pagos; null si aún no se sabe.
 * @param {Function} [props.onVerSaldo] Lleva al plan de pagos, donde se calcula el saldo.
 */
const ResumenCredito = ({ credito, saldo = null, mora = null, onVerSaldo }) => {
  const cargando = !credito;

  const cargandoDato = (ancho) => (
    <Skeleton variant="text" width={ancho} sx={{ fontSize: "1.25rem", ...sinAnimacion }} />
  );

  const valorMonto = (valor) =>
    cargando ? (
      cargandoDato(90)
    ) : (
      <Typography variant="monto" component="span">
        {formatearMonto(valor)}
      </Typography>
    );

  const valorFecha = (valor) =>
    cargando ? (
      cargandoDato(90)
    ) : (
      <Typography
        component="span"
        sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
      >
        {fechaDeCredito(valor)}
      </Typography>
    );

  // El saldo se calcula al cargar el plan de pagos: hasta entonces se invita a abrirlo.
  let contenidoSaldo;
  if (cargando) {
    contenidoSaldo = cargandoDato(90);
  } else if (saldo === null) {
    contenidoSaldo = (
      <>
        <Typography component="span" sx={{ fontWeight: 700 }}>
          —
        </Typography>
        {onVerSaldo && (
          <Button
            size="small"
            variant="text"
            onClick={onVerSaldo}
            sx={{ display: "flex", px: 0, minWidth: 0 }}
          >
            Ver en el plan de pagos
          </Button>
        )}
      </>
    );
  } else {
    contenidoSaldo = (
      <Typography variant="monto" component="span">
        {formatearMonto(saldo)}
      </Typography>
    );
  }

  return (
    <Paper
      component="section"
      aria-label="Resumen del crédito"
      aria-busy={cargando || undefined}
      sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}
    >
      <Box
        component="dl"
        sx={{
          m: 0,
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
            lg: "repeat(7, minmax(0, 1fr))",
          },
        }}
      >
        <Dato etiqueta="Crédito">
          {cargando ? (
            cargandoDato(60)
          ) : (
            <Typography
              component="span"
              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
            >
              {`#${credito.id}`}
            </Typography>
          )}
        </Dato>

        <Dato etiqueta="Estado">
          {cargando ? (
            <Skeleton
              variant="rounded"
              width={90}
              height={24}
              sx={{ borderRadius: 3, ...sinAnimacion }}
            />
          ) : (
            <EstadoChip {...estadoDeCredito(credito, mora)} />
          )}
        </Dato>

        <Dato etiqueta="Monto entregado">{valorMonto(credito?.montoEntregado)}</Dato>
        <Dato etiqueta="Cuota">{valorMonto(credito?.cuotaPagar)}</Dato>

        <Dato etiqueta="Saldo">{contenidoSaldo}</Dato>

        <Dato etiqueta="Fecha de entrega">{valorFecha(credito?.fechaEntrega)}</Dato>
        <Dato etiqueta="Vencimiento">{valorFecha(credito?.fechaVencimiento)}</Dato>
      </Box>
    </Paper>
  );
};

export default ResumenCredito;
